import React, { ReactNode, useMemo } from 'react';

import { Box, styled } from '@mui/material';
import { captureException } from '@sentry/react';
import cx from 'classnames';
import parse from 'html-react-parser';
import { ErrorBoundary } from 'react-error-boundary';

import IKImage from '~/components/IKImage/IKImage';
import MathJaxBlock from '~/components/MathJaxBlock/MathJaxBlock';
import { isFormulaRegExp } from '~/hooks/useMathJaxScriptLoader';
import { PAYWALLED_CONTENT_SELECTORS } from '~/pages/SingleVideoPage/const';
import { CssUtilityType } from '~/styled';
import { BREAKPOINTS_VALUES, MIDDLE_MARGIN_PX, SMALL_MARGIN_PX } from '~/theme';
import TextWithTerms from '~/ui/components/TextWithTerms/TextWithTerms';

enum BlockTypes {
  image = 'image',
  html = 'html',
}

function ImageComponent({ src, alt }) {
  return (
    <ImageWrapper>
      <IKImage
        path={src}
        alt={alt}
        draggable={false}
        srcset={[{ width: 400 }, { width: 700 }, { width: 800 }, { width: 900 }]}
        sizes={`(max-width:${BREAKPOINTS_VALUES['sm']}px) 400px, (max-width:${BREAKPOINTS_VALUES['md']}px) 700px, (max-width:${BREAKPOINTS_VALUES['lg']}px) 800px, 900px`}
      />
    </ImageWrapper>
  );
}

interface SummaryProps {
  summary?: string | null;
  wrapperCss?: CssUtilityType;
  children?: ReactNode;
}

function createHtmlBlock(html) {
  return {
    layout: BlockTypes.html,
    attributes: { html },
  };
}

const Summary: React.FC<SummaryProps> = ({ summary, wrapperCss, children }) => {
  const parsedSummary = summary && JSON.parse(summary);
  const summaryContentBlocks = useMemo(
    () => (typeof parsedSummary === 'string' ? [createHtmlBlock(parsedSummary)] : parsedSummary),
    [parsedSummary],
  );

  return (
    <Wrapper
      className={cx(PAYWALLED_CONTENT_SELECTORS.SUMMARY, 'wrapperWithStyledTable')}
      optionalStyles={wrapperCss}
      data-testid="videoSummary"
    >
      {Array.isArray(summaryContentBlocks) &&
        summaryContentBlocks.map((block) => {
          if (block.layout === BlockTypes.image) {
            return (
              <ImageComponent
                key={block.key}
                src={block.attributes.image}
                alt={block.attributes.alt}
              />
            );
          }

          if (block.layout === BlockTypes.html) {
            return (
              <MathJaxBlock
                key={block.key}
                hasFormula={!!block.attributes?.html?.match(isFormulaRegExp)}
              >
                <ErrorBoundary
                  fallback={<div dangerouslySetInnerHTML={{ __html: block.attributes?.html }} />}
                  onError={() => {
                    captureException(
                      new Error(`URGENT! Parsing error in content: "${block.attributes?.html}"`),
                    );
                  }}
                >
                  <TextWithTerms>{parse(block.attributes?.html)}</TextWithTerms>
                </ErrorBoundary>
              </MathJaxBlock>
            );
          }

          return null;
        })}
      {children}
    </Wrapper>
  );
};

export default Summary;

const ImageWrapper = styled(Box)`
  padding: ${SMALL_MARGIN_PX};
  margin-top: ${SMALL_MARGIN_PX};
  margin-bottom: ${SMALL_MARGIN_PX};
  background: ${({ theme }) => theme.palette.secondary.light};
  display: flex;
  justify-content: center;
  border-radius: 5px;

  & img {
    max-width: 100%;
    height: auto;
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    padding: ${MIDDLE_MARGIN_PX};
    margin-top: ${MIDDLE_MARGIN_PX};
    margin-bottom: ${MIDDLE_MARGIN_PX};
  }
`;
const Wrapper = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'optionalStyles',
})<{ optionalStyles?: CssUtilityType }>`
  overflow-x: auto;
  max-width: 100%;
  font-size: 16px;
  line-height: 1.5;
  color: ${({ theme }) => theme.palette.common.text.secondaryToPrimary};

  & h1,
  & h2,
  & h3,
  & h4,
  & h5,
  & h6,
  & p,
  & ul,
  & ol {
    margin-bottom: 18px;
  }

  & h1,
  & h2,
  & h3,
  & h4,
  & h5,
  & h6 {
    color: ${({ theme }) => theme.palette.common.white};
    line-height: 1.5;
  }

  & ul,
  & ol {
    padding-left: 20px;

    & li {
      &::marker {
        color: ${({ theme }) => theme.palette.common.primary};
      }
    }
  }

  & svg g {
    fill: ${({ theme }) => theme.palette.common.white};
    stroke: ${({ theme }) => theme.palette.common.white};
  }

  ${({ theme }) => theme.breakpoints.up('md')} {
    font-size: 18px;
    margin-bottom: 30px;
  }

  ${({ optionalStyles }) => optionalStyles}
`;
